import { For, createSignal, onMount, onCleanup } from 'solid-js';
import { products } from '~/data/products';
import { ProductCard } from '~/components/ProductCard';
import './ProductsSlider.css';

interface Props {
  onRequestClick?: () => void;
}

export function ProductsSlider(props: Props) {
  let trackRef: HTMLDivElement | undefined;
  const [canPrev, setCanPrev] = createSignal(false);
  const [canNext, setCanNext] = createSignal(true);

  const updateButtons = () => {
    if (!trackRef) return;
    const { scrollLeft, scrollWidth, clientWidth } = trackRef;
    setCanPrev(scrollLeft > 4);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 4);
  };

  const scrollByCards = (dir: 1 | -1) => {
    if (!trackRef) return;
    const card = trackRef.querySelector<HTMLElement>('.product-card');
    const step = card ? card.clientWidth + 24 : trackRef.clientWidth * 0.8;
    trackRef.scrollBy({ left: step * dir, behavior: 'smooth' });
  };

  onMount(() => {
    updateButtons();
    if (!trackRef) return;
    const track = trackRef;
    track.addEventListener('scroll', updateButtons, { passive: true });
    const ro = new ResizeObserver(updateButtons);
    ro.observe(track);
    onCleanup(() => {
      track.removeEventListener('scroll', updateButtons);
      ro.disconnect();
    });
  });

  return (
    <section class="section section--tight products" id="products">
      <div class="container">
        <div class="products__head" data-reveal>
          <div class="eyebrow">Каталог</div>
          <h2>Двигатели в&nbsp;наличии и&nbsp;у&nbsp;поставщиков</h2>
          <p class="section-head__lead">
            Перед оплатой проверяем наличие, комплектацию и совместимость двигателя с вашей техникой.
            Листайте каталог стрелками или свайпом.
          </p>
        </div>
      </div>

      <div class="products__viewport" data-reveal>
        <button
          type="button"
          class="products__arrow products__arrow--prev"
          aria-label="Предыдущий двигатель"
          disabled={!canPrev()}
          onClick={() => scrollByCards(-1)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div class="container products__container">
          <div class="products__track" ref={(el) => (trackRef = el)} role="list">
            <For each={products}>
              {(product) => (
                <div class="products__slide" role="listitem">
                  <ProductCard product={product} />
                </div>
              )}
            </For>
          </div>
        </div>

        <button
          type="button"
          class="products__arrow products__arrow--next"
          aria-label="Следующий двигатель"
          disabled={!canNext()}
          onClick={() => scrollByCards(1)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <span
          class="products__edge products__edge--left"
          classList={{ 'products__edge--show': canPrev() }}
          aria-hidden="true"
        />
        <span
          class="products__edge products__edge--right"
          classList={{ 'products__edge--show': canNext() }}
          aria-hidden="true"
        />
      </div>

      <div class="container products__foot" data-reveal>
        <button class="btn btn--primary" onClick={props.onRequestClick}>
          Запросить подбор агрегата <span class="btn__arrow">→</span>
        </button>
        <p class="products__foot-note">
          Можно прислать VIN, серийный номер агрегата или фото шильдика — я&nbsp;уточню модификацию.
        </p>
      </div>
    </section>
  );
}
